import React, { useState, useRef, useEffect } from 'react';
import { ReactComponent as SearchIcon } from "./images/icon-search.svg";
import { ReactComponent as CloseIcon } from "./images/icon-close.svg";

const Autocomplete = ({ suggestions, onUserInput, clearInput, inputValue, focusChanged, disabled }) => {
  const [internalValue, setInternalValue] = useState(inputValue || '');
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef(null);

  // Sync internal value with inputValue prop
  useEffect(() => {
    setInternalValue(inputValue);
  }, [inputValue]);

  // Handle input change and filter suggestions
  const handleChange = (e) => {
    const input = e.target.value;
    setInternalValue(input);
    onUserInput(input);

    const filtered = suggestions.filter((suggestion) =>
      suggestion.toLowerCase().includes(input.toLowerCase())
    );

    setFilteredSuggestions(filtered);
    setShowSuggestions(true);
    setActiveSuggestionIndex(-1);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    setInternalValue(suggestion);
    onUserInput(suggestion);
    setShowSuggestions(false);
  };

  const handleFocus = () => {
    focusChanged(true, 'generalFilter');
  };

  const handleBlur = () => {
    focusChanged(false, '');
    setShowSuggestions(false)
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.keyCode === 13) {
      // Enter key
      if (activeSuggestionIndex === -1) {
        onUserInput(internalValue);
        setShowSuggestions(false);
      } else if (filteredSuggestions[activeSuggestionIndex]) {
        handleSuggestionClick(filteredSuggestions[activeSuggestionIndex]);
      }
    } else if (e.keyCode === 38) {
      // Up arrow key
      e.preventDefault();
      if (activeSuggestionIndex > 0) {
        setActiveSuggestionIndex(activeSuggestionIndex - 1);
      } else {
        setActiveSuggestionIndex(-1);
      }
    } else if (e.keyCode === 40) {
      // Down arrow key
      e.preventDefault();
      if (activeSuggestionIndex < filteredSuggestions.length - 1) {
        setActiveSuggestionIndex(activeSuggestionIndex + 1);
      }
    }
  };

  const SuggestionsListComponent = () => (
    <div className="suggestions-container" ref={suggestionsRef}>
      {filteredSuggestions.length > 0 ? (
        <ul className="suggestions">
          {filteredSuggestions.map((suggestion, index) => (
            <li
              key={suggestion}
              className={`suggestion-item ${index === activeSuggestionIndex ? 'active' : ''}`}
              onMouseDown={() => handleSuggestionClick(suggestion)}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      ) : (
        <ul className="suggestions">
          <li className="suggestion-item">No matching suggestions!</li>
        </ul>
      )}
    </div>
  );

  return (
    <div className="autocomplete">
      <SearchIcon className="search-icon" />
      <input
        type="text"
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        value={internalValue}
        placeholder="Search..."
        className={`autocomplete-input ${internalValue ? 'user-typed' : ''}`}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
      />
      {internalValue && (
        <div className="clear-buttons">
          <CloseIcon className="close-icon" onClick={() => clearInput()} />
        </div>
      )}
      {showSuggestions && internalValue && <SuggestionsListComponent />}
    </div>
  );
};

export default Autocomplete;
